/**
 * Kiosk token management dialog. Lists tokens for a specific storage,
 * lets a manager create new tokens (returns the raw token string ONCE)
 * and revoke existing ones. Mounted from `StoragesPage` or wherever
 * storage management lives.
 */
import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import API from '../../services/api';
import MobileDialog from './MobileDialog';

const KIOSK_ROUTE = '/kiosk/scanner';

const KioskTokensDialog = ({ open, onClose, storage }) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creatingForm, setCreatingForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newAllowReceive, setNewAllowReceive] = useState(true);
  const [newAllowDeduct, setNewAllowDeduct] = useState(true);
  const [newAllowCount, setNewAllowCount] = useState(true);
  const [newTokenResult, setNewTokenResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await API.get('/kiosk-tokens/list/', {
        params: { StorageID: storage?.StorageID },
      });
      setTokens(data.tokens || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאה בטעינת הקישורים');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && storage?.StorageID) {
      load();
      setNewTokenResult(null);
      setCreatingForm(false);
      setCopied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storage?.StorageID]);

  const handleCreate = async () => {
    setSubmitting(true);
    setError('');
    setCopied(false);
    try {
      const { data } = await API.post('/kiosk-tokens/create/', {
        StorageID: storage.StorageID,
        Label: newLabel.trim(),
        AllowReceive: newAllowReceive,
        AllowDeduct: newAllowDeduct,
        AllowCount: newAllowCount,
      });
      const origin = window.location.origin;
      const url = `${origin}${KIOSK_ROUTE}/${data.Token}`;
      setNewTokenResult({ ...data, kioskUrl: url });
      setCreatingForm(false);
      setNewLabel('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאה ביצירת קישור');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    try {
      await API.post('/kiosk-tokens/revoke/', { TokenID: tokenId });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאה בביטול הקישור');
    }
  };

  const copy = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {});
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="primary" />
          עמדת מלאי — {storage?.StorageName || ''}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          צור קישור לעמדת מלאי שתרוץ על מחשב במחסן, ללא צורך בשם משתמש וסיסמה. כל קישור מאפשר לעובדי המחסן לקבל, לנכות ולספור מלאי — רק למחסן הזה.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Newly created link — show once ── */}
        {newTokenResult && (
          <Alert
            severity="success"
            icon={<LinkIcon />}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              הקישור נוצר בהצלחה!
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              העתק את הקישור ושמור אותו — הוא לא יוצג שוב. פתח אותו בדפדפן על מחשב המחסן.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={newTokenResult.kioskUrl}
              InputProps={{
                readOnly: true,
                sx: { direction: 'ltr', fontFamily: 'monospace', fontSize: '0.85rem' },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'הועתק!' : 'העתק קישור'}>
                      <IconButton size="small" onClick={() => copy(newTokenResult.kioskUrl)} color={copied ? 'success' : 'default'}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="פתח עמדה בלשונית חדשה">
                      <IconButton
                        size="small"
                        onClick={() => window.open(newTokenResult.kioskUrl, '_blank')}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Alert>
        )}

        {/* ── Create new link form ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            קישורים פעילים
          </Typography>
          {!creatingForm && (
            <Button
              variant="contained"
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => setCreatingForm(true)}
            >
              צור קישור חדש
            </Button>
          )}
        </Box>

        {creatingForm && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              bgcolor: '#f8fafc',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              יצירת קישור לעמדת מלאי חדשה
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="שם העמדה"
              placeholder="לדוגמה: מחשב שער כניסה, טאבלט מחסן צפוני"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              helperText="תן שם שיעזור לך לזהות איפה העמדה הזו נמצאת"
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              מה העמדה הזו יכולה לעשות?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newAllowReceive}
                    onChange={(e) => setNewAllowReceive(e.target.checked)}
                    color="success"
                  />
                }
                label="קבלת סחורה"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newAllowDeduct}
                    onChange={(e) => setNewAllowDeduct(e.target.checked)}
                    color="error"
                  />
                }
                label="הוצאת סחורה"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newAllowCount}
                    onChange={(e) => setNewAllowCount(e.target.checked)}
                    color="info"
                  />
                }
                label="ספירת מלאי"
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              ניתן להגביל את העמדה לפעולות מסוימות בלבד. לדוגמה, עמדה ברמפת קבלה תצטרך רק "קבלת סחורה".
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setCreatingForm(false)} disabled={submitting}>
                ביטול
              </Button>
              <Button
                variant="contained"
                onClick={handleCreate}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                disabled={
                  submitting ||
                  !newLabel.trim() ||
                  !(newAllowReceive || newAllowDeduct || newAllowCount)
                }
              >
                {submitting ? 'יוצר...' : 'צור קישור'}
              </Button>
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* ── Existing links table ── */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : tokens.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              עדיין לא נוצרו קישורים למחסן הזה
            </Typography>
            <Typography variant="caption" color="text.secondary">
              לחץ "צור קישור חדש" כדי ליצור עמדת מלאי במחסן
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>שם עמדה</TableCell>
                <TableCell>פעולות מותרות</TableCell>
                <TableCell>נוצר</TableCell>
                <TableCell>מצב</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((t) => {
                const modes = [];
                if (t.AllowReceive) modes.push('קבלה');
                if (t.AllowDeduct) modes.push('לקיחה');
                if (t.AllowCount) modes.push('ספירה');
                return (
                  <TableRow key={t.TokenID}>
                    <TableCell>{t.Label}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {modes.map((m) => (
                          <Chip key={m} label={m} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {t.CreatedAt ? new Date(t.CreatedAt).toLocaleDateString('he-IL') : '—'}
                    </TableCell>
                    <TableCell>
                      {t.IsActive ? (
                        <Chip label="פעיל" size="small" color="success" />
                      ) : (
                        <Chip label="בוטל" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {t.IsActive && (
                        <Tooltip title="בטל קישור זה — העמדה תפסיק לעבוד">
                          <IconButton size="small" color="error" onClick={() => handleRevoke(t.TokenID)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>סגור</Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default KioskTokensDialog;
